/**
 * Created by Carsten Hibbeler on 27.03.2019.
 */
import { AppContext, deepEqual, RemoteDataActions } from 'plugin/liferay-widgets-react-web';
import { FeatureManager, GeoJsonActions, getFeatureId, getGeoJsonStore } from 'plugin/map-portlet';
import ModalActions from '../actions/ModalActions';
import L from 'external/leaflet';

const toMap = (array, idFtkn) =>
    array.reduce((obj, item) => {
        obj[idFtkn(item)] = item;
        return obj;
    }, {});

const clone = s => {
    const t = {};
    for (let k in s) {
        if (Object.prototype.hasOwnProperty.call(s, k)) {
            t[k] = s[k];
        }
    }
    return t;
};

const diff = (oldFeaturesMap, newFeatures, oldFeatureProps, newFeatureProps) => {
    const t0 = performance.now();
    oldFeaturesMap = clone(oldFeaturesMap);
    const diff = {
        added: [],
        removed: [],
        changed: [],
    };
    newFeatures.forEach(nF => {
        const id = getFeatureId(nF),
            oF = oldFeaturesMap[id];
        if (!oF) {
            diff.added.push(nF);
        } else {
            if (!deepEqual(nF, oF) || !deepEqual(oldFeatureProps[id], newFeatureProps[id])) {
                diff.changed.push(nF);
            }
            delete oldFeaturesMap[id];
        }
    });
    for (let k in oldFeaturesMap) {
        if (Object.prototype.hasOwnProperty.call(oldFeaturesMap, k)) {
            diff.removed.push(oldFeaturesMap[k]);
        }
    }
    const dt = performance.now() - t0;
    if (dt > 3) {
        console.log('Diff took ' + dt + ' milliseconds.');
    }
    return diff;
};

const geoJsonStore = getGeoJsonStore(AppContext);

const serviceToClass = {
    '/critical-object-portlet.criticalobject/': 'CriticalObject',
    '/frb-portlet.floodretentionbasin/': 'FloodRetentionBasin',
    '/fliwas-structure-portlet.fliwasorganizationapi/': 'Group',
    '/news-portlet.newsmessage/': 'NewsMessage',
    '/precipitation-portlet.precipitationstation/': 'PrecipitationStation',
    '/flood-protection-object-portlet.floodprotectionobjectline/': 'FloodProtectionObjectLine',
    '/flood-protection-object-portlet.floodprotectionobjectpoint/': 'FloodProtectionObjectPoint',
    '/twodfm-portlet.dikebreak/': 'DikeBreak'
};

//TODO UGLY
const MissingRendererException = {};

export default class BaseRemoteDataLayerController {

    /**
     * Use this to mutate layer config used for layer creation.
     *
     * @param {object} layerConfig - options
     * @return {object} layerConfig - options
     */
    layerConfig(layerConfig){
        if (layerConfig.servicePath) {
            /* Old style config */
            layerConfig = {
                ...layerConfig,
                params: [serviceToClass[layerConfig.servicePath]],
            };
            delete layerConfig['servicePath'];
            delete layerConfig['method'];
        }

        return layerConfig;
    }

    /**
     * Invoked once after layer init.
     *
     * @param {Map} map - leaflet map
     * @param {object} o - global options for the map
     * @param {Layer} layer - leaflet Layer
     * @param {object} layerConfig - config of the layer
     */
    init(map, o, layer, layerConfig, context){
        this.map = map;
        this.o = o;
        this.layer = layer || (layer && layer.getInternalLayer && layer.getInternalLayer());
        this.context = context;
        //    this.onEachFeature.bind(this);
        this.featureLayerProps = {};
        this.update = this.update.bind(this);
        this.getOptions = this.getOptions.bind(this);
        this.onZoomeEnd = this.onZoomeEnd.bind(this);

        map.on('zoomend', this.onZoomeEnd);

        this.dataName = layer.id;//config.servicePath + config.method;
        this.removeListener = geoJsonStore.addListener(() => this.forceUpdate());
        GeoJsonActions.geoJson(this.dataName, layerConfig.params);

        this._features = {};
        this._featureLayers = {};
        this._featureProps = {};
    }

    forceUpdate(){
        this.update(this.map, this.getOptions());
    }

    onZoomeEnd(){
        this.forceUpdate();
    }

    getOptions(){
        return this.o;
    }

    /**
     * Config phase.
     * Use this to mutate options pre <code>update</code>.
     * Do not use the options for third party component updates.
     *
     * @param {object} o - options
     * @return {object} o - options
     */
    options(o){
        //TODO This seems wrong because the selected layers may not exists at this moment.
        const layer = this.getLayer(),
            { selectedItems } = o;

        if (selectedItems && layer.eachLayer) {
            const selectedIds = toMap(selectedItems, i => i.className + (i.item || i).id),
                selectedLayers = o.selectedLayers ? o.selectedLayers.slice() : [];

            layer.eachLayer(featureLayer => {
                if (selectedIds[getFeatureId(featureLayer.feature)]) {
                    selectedLayers.push(featureLayer);
                }
            });

            if (selectedLayers.length !== selectedItems.length) {
                console.warn('Missing ' + (selectedItems.length - selectedLayers.length) + ' selected layers');
            }

            if (selectedLayers.length > 0) {
                o.selectedLayers = selectedLayers;
            }
        }
        return o;
    }

    /**
     * Update phase.
     * Use this to update based on the global map options.
     * Not invoked after first init.
     *
     * @param {Map} map - leaflet map
     * @param {object} o - options
     */
    update(map, o){
        console.log('BaseRemoteDataLayerController update()', { map, o });
        this.o = o;

        const remoteData = geoJsonStore.getRemoteData(this.dataName),
            features = remoteData && remoteData.item && remoteData.item.features || [],
            selectedIds = o.selectedItems && toMap(o.selectedItems, i => i.className + (i.item || i).id) || {};

        const featureProps = features.reduce((props, feature) => {
            const id = getFeatureId(feature),
                selected = selectedIds[id],
                zoomThreshold = this.getZoomThreshold(feature);

            if (zoomThreshold || selected) {
                props[id] = {
                    highlight: selected,
                    zoomThreshold,
                    collapsed: typeof zoomThreshold === 'number' && this.map.getZoom() <= zoomThreshold
                };
            }
            return props;
        }, {});

        this.updateFeatures(features, featureProps);

    }

    updateFeatures(features, featureProps){
        const layer = this.getLayer(),
            addedLayers = [],
            removedLayers = [],
            { added, changed, removed } = diff(this._features, features, this._featureProps, featureProps);

        if (added.length + changed.length + removed.length === 0) {
            return;
        }

        //Remove missing features
        removed.forEach(feature => {
            const fId = getFeatureId(feature);
            removedLayers.push(this._featureLayers[fId]);
            delete this._featureLayers[fId];
            delete this._features[fId];
            delete this._featureProps[fId];
        });

        const addFeature = (feature) => {
            const fId = getFeatureId(feature),
                props = featureProps[fId],
                l = FeatureManager.createFeatureLayer(feature, props, true);

            if (l.then) {
                l.then(() => this.forceUpdate());
                throw MissingRendererException; //Early stop layer update to prevent massive updates
            } else {
                if (props && props.highlight) {
                    l.preventCluster = true;
                }
                this.prepareLayerControls(feature, l);
                addedLayers.push(l);
                this._featureLayers[fId] = l;
                this._features[fId] = feature;
                if (props) {
                    this._featureProps[fId] = props;
                } else {
                    delete this._featureProps[fId];
                }
            }
        };

        try {
            //Remove old and add new features
            changed.forEach(feature => {
                removedLayers.push(this._featureLayers[getFeatureId(feature)]);
                addFeature(feature);
            });

            //Add new features
            added.forEach(addFeature);

        } catch (e) {
            if (e !== MissingRendererException) {
                throw e;
            } else {
                console.warn('Missing renderer');
            }
        }

        layer.removeLayers(removedLayers);
        layer.addLayers(addedLayers);
    }

    remove(map){
        map.off('zoomend', this.onZoomeEnd);
        if (this.removeListener) {
            this.removeListener.remove();
        }
    }

    getLayer(){
        if (this.layer.getInternalLayer && this.layer.getInternalLayer()) {
            this.layer = this.layer.getInternalLayer();
        }
        return this.layer;
    }

    prepareLayerControls(feature, featureLayer){
        if (!this.onMouseOver) {
            //Create only one function for all layers
          this.onMouseOver = e => e && e.target && e.target.openTooltip && e.target.openTooltip();
            this.onMouseOut = e => e && e.target && e.target.closeTooltip && e.target.closeTooltip();
        }
        featureLayer.bindTooltip(feature.properties.title, {
            autoPan: false,
            keepInView: true,
            closeOnClick: false,
            direction: 'top',
            offset: [0, -40]
        });
        featureLayer.on('mouseover', this.onMouseOver);
        featureLayer.on('mouseout', this.onMouseOut);
        featureLayer.on('click', this.onFeatureClick.bind(this, feature));
    }

    getZoomThreshold(feature, minSize = 50){
        if (feature.geometry.type === 'Point') {
            return null;
        }
        const id = getFeatureId(feature),
            currentProps = this._featureProps[id];

        //Reuse zoomThreshold if the geometry is the same
        let zoomThreshold;
        if (currentProps && currentProps.zoomThreshold >= 0 && deepEqual(this._features[id].geometry, feature.geometry)) {
            zoomThreshold = currentProps.zoomThreshold;
        } else {
            const layer = L.GeoJSON.geometryToLayer(feature);
            if (layer instanceof L.Circle) {
                //https://github.com/oliverroick/Leaflet.Deflate/blob/master/src/L.Deflate.js
                throw new Error('Circle is not supported');
            }
            zoomThreshold = DeflateUtil.getZoomThreshold(layer.getBounds(), this.map, minSize);
        }
        return zoomThreshold;
    }

  onFeatureClick(feature) {
    this.onItemClick(feature.properties.type, feature.properties.id, feature.properties.title, feature.properties.typeKey);
  }

    onItemClick(className, id){
        RemoteDataActions.pageSelected(className, { id });
        ModalActions.showModal(className, { id }, false, undefined, { contextId: this.context && this.context.contextId });
    }
}

const DeflateUtil = {

    getZoomThreshold: function (bounds, map, minSize){
        var zoomThreshold = null;
        var zoom = map.getZoom();

        if (this.isCollapsed(bounds, map, zoom, minSize)) {
            while (!zoomThreshold) {
                zoom += 1;
                if (!this.isCollapsed(bounds, map, zoom, minSize)) {
                    zoomThreshold = zoom - 1;
                }
            }
        } else {
            while (!zoomThreshold) {
                zoom -= 1;
                if (this.isCollapsed(bounds, map, zoom, minSize)) {
                    zoomThreshold = zoom;
                }
            }
        }
        return zoomThreshold;
    },

    /**
     * Check if a path is displayed smaller than minSize (in pixels)
     */
    isCollapsed: function (bounds, map, zoom, minSize = 50){
        const ne_px = map.project(bounds.getNorthEast(), zoom);
        const sw_px = map.project(bounds.getSouthWest(), zoom);

        const width = ne_px.x - sw_px.x;
        const height = sw_px.y - ne_px.y;
        return (height < minSize || width < minSize);
    }
};








// WEBPACK FOOTER //
// ./js/map/BaseRemoteDataLayerController.js